import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ico, autoImgs, burnerImgs, EDM_VIDEO } from '../shared';


export default function Home() {
  useEffect(() => {
    document.title = 'Ratnam Techo Inc. | Precision Engineering & Custom Fabrication';
  }, []);

  return (
    <>
      <Hero />
      <About />
      <Services />
      <WireEDM />
      <Why />
      <GalleryPreview />
      <Industries />
      <CTA />
    </>
  );
}

/* ── Hero ── */
function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-bg" />
      <div className="hero-grid" />
      <video autoPlay muted loop playsInline className="hero-video">
        <source src={EDM_VIDEO} type="video/mp4" />
      </video>
      <div className="hero-video-overlay" />
      <div className="hero-glow" />
      <div className="hero-glow-2" />

      <div className="hero-content">
        <div className="hero-badge">
          <span className="hero-badge-dot" />
          <span className="hero-badge-text">Precision Engineering &amp; Custom Fabrication</span>
        </div>

        <h1>
          Glass Burners &amp;<br />
          <span className="gold">Custom Fabrication</span><br />
          Built to Perform
        </h1>

        <p className="hero-desc">
          We design, build, and refine high-performance glass burners/torches and
          custom-engineered solutions for professionals who demand reliability,
          precision, and durability.
        </p>

        <p className="hero-tagline">
          From concept to creation — we turn your ideas into functional, high-quality products.
        </p>

        <div className="hero-actions">
          <a
            href="https://wa.me/14164172091"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary cta-btn cta-btn-whatsapp"
          >
            <Ico name="whatsapp" size={20} />
            WhatsApp Us
          </a>
          <Link to="/services" className="btn-outline">
            Explore Services
          </Link>
        </div>

        <div className="hero-stats">
          <div className="hero-stat-item">
            <div className="hero-stat-num">10<span>+</span></div>
            <div className="hero-stat-label">Years Experience</div>
          </div>
          <div className="hero-stat-item">
            <div className="hero-stat-num">500<span>+</span></div>
            <div className="hero-stat-label">Projects Completed</div>
          </div>
          <div className="hero-stat-item">
            <div className="hero-stat-num">100<span>%</span></div>
            <div className="hero-stat-label">Custom Solutions</div>
          </div>
          <div className="hero-stat-item">
            <div className="hero-stat-num">24<span>/7</span></div>
            <div className="hero-stat-label">Mobile Support</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── About Preview ── */
function About() {
  const points = [
    { icon: 'microscope', text: 'Specialized in manufacturing premium glass burners/torches' },
    { icon: 'settings',   text: 'Advanced fabrication solutions tailored to your needs' },
    { icon: 'palette',    text: 'Supporting artists, industrial users, and businesses' },
    { icon: 'wrench',     text: 'Custom prototypes, machining work, and equipment repair' },
  ];

  return (
    <section className="section about" id="about">
      <div className="section-inner">
        <div className="about-grid">
          <div className="about-visual">
            <div className="about-img-wrap">
              <img src={autoImgs[0]} alt="Precision engineering work" className="about-photo-main" />
              <div className="about-photo-row">
                <img src={autoImgs[1]} alt="Custom fabrication" className="about-photo-thumb" />
                <img src={autoImgs[2]} alt="Engineering excellence" className="about-photo-thumb" />
              </div>
            </div>
            <div className="about-badge">
              <div className="about-badge-num">10+</div>
              <div className="about-badge-text">Years of<br />Excellence</div>
            </div>
          </div>

          <div>
            <div className="section-tag">
              <span className="section-tag-line" />
              About Us
            </div>
            <h2 className="section-title">
              Craftsmanship Meets<br />
              <span className="blue">Precision Engineering</span>
            </h2>
            <p className="section-sub">
              At Ratnam Techo Inc., we specialize in manufacturing premium glass burners/torches
              and delivering advanced fabrication solutions tailored to your needs. With a focus
              on craftsmanship, innovation, and precision engineering, we bring technical expertise
              and hands-on experience to every project.
            </p>
            <div className="about-points">
              {points.map((p, i) => (
                <div className="about-point" key={i}>
                  <div className="about-point-icon"><Ico name={p.icon} size={18} /></div>
                  <div className="about-point-text">{p.text}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-primary" style={{ marginTop: 28, display: 'inline-flex' }}>
              Learn More About Us →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Services Preview ── */
function Services() {
  const cards = [
    { icon: 'flame',    title: 'Glass Burner / Torch Solutions',      desc: 'High-performance glass burners and torches built for professional use.', featured: true },
    { icon: 'zap',      title: 'Prototyping & Product Development',    desc: 'From design to production with precision accuracy and rapid turnaround.' },
    { icon: 'settings', title: 'Machining & Fabrication',              desc: 'Full-service machining, welding, and custom fabrication capabilities.' },
    { icon: 'wrench',   title: 'Repair & Maintenance',                 desc: 'Expert repair, servicing, and performance optimization for your equipment.' },
  ];

  return (
    <section className="section services" id="services">
      <div className="section-inner">
        <div className="services-header">
          <div>
            <div className="section-tag">
              <span className="section-tag-line" />
              Our Services
            </div>
            <h2 className="section-title">
              End-to-End <span className="gold">Engineering</span><br />Solutions
            </h2>
          </div>
          <p className="section-sub">
            From prototype to production, repair to optimization — we handle every stage with precision.
          </p>
        </div>

        <div className="services-grid">
          {cards.map((card, i) => (
            <div className={`service-card ${card.featured ? 'featured' : ''}`} key={i}>
              <div className="service-icon"><Ico name={card.icon} size={28} /></div>
              <h3 className="service-title">{card.title}</h3>
              <p className="service-desc">{card.desc}</p>
            </div>
          ))}
          <div className="mobile-service-card">
            <div className="mobile-service-icon"><Ico name="truck" size={34} /></div>
            <div>
              <h3 className="mobile-service-title">Mobile Service</h3>
              <p className="mobile-service-desc">
                We come to you. On-site support for repairs and maintenance — wherever you are.
              </p>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Link to="/services" className="btn-primary btn-gold">
            View All Services →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Wire EDM Feature ── */
function WireEDM() {
  const features = [
    'Extreme dimensional accuracy (±0.001")',
    'Cuts any electrically conductive material',
    'Complex geometry without tooling pressure',
    'No thermal distortion on workpiece',
    'Ideal for hardened steel, titanium & exotic alloys',
  ];

  return (
    <section className="wire-edm-section" id="wire-edm">
      <div className="wire-edm-inner">
        <div className="wire-edm-video-col">
          <div className="wire-edm-video-wrap">
            <video autoPlay muted loop playsInline>
              <source src={EDM_VIDEO} type="video/mp4" />
            </video>
            <div className="wire-edm-live-badge">
              <span className="wire-edm-live-dot" />
              Live Wire EDM Process
            </div>
          </div>
        </div>
        <div className="wire-edm-content-col">
          <div className="section-tag wire-edm-tag">
            <span className="section-tag-line" />
            Advanced Capability
          </div>
          <h2 className="section-title wire-edm-title">
            Wire EDM<br />
            <span className="gold">Precision Cutting</span>
          </h2>
          <p className="section-sub wire-edm-sub">
            Wire Electrical Discharge Machining delivers unmatched accuracy for complex
            profiles and tight tolerances. We cut any conductive material with precision
            that traditional machining simply can't match.
          </p>
          <ul className="wire-edm-features">
            {features.map((f, i) => (
              <li key={i}><span className="wire-edm-check">✓</span>{f}</li>
            ))}
          </ul>
          <a
            href="https://wa.me/14164172091"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary btn-gold"
            style={{ marginTop: 36, display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
          >
            <Ico name="whatsapp" size={18} />
            Discuss Your Project
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── Why Choose Us ── */
function Why() {
  const reasons = [
    { icon: 'target', title: 'Precision Engineering',  desc: 'Every product is built with meticulous accuracy and exceptional attention to detail.' },
    { icon: 'layers', title: 'Custom Solutions',        desc: 'Tailored services to match your exact specifications and project requirements.' },
    { icon: 'gem',    title: 'Reliable Performance',    desc: 'Durable tools and components designed for consistent long-term performance.' },
    { icon: 'clock',  title: 'Fast Turnaround',         desc: 'Efficient processes and streamlined workflows to meet your tightest deadlines.' },
    { icon: 'shield', title: 'End-to-End Support',      desc: 'From initial design through repair and maintenance — we handle everything.' },
  ];

  return (
    <section className="section why" id="why">
      <div className="why-bg" />
      <div className="section-inner" style={{ position: 'relative', zIndex: 2 }}>
        <div className="section-tag">
          <span className="section-tag-line" />
          Why Choose Us
        </div>
        <h2 className="section-title">The Ratnam Techo<br /><span className="gold">Advantage</span></h2>
        <p className="section-sub">
          We combine hands-on expertise with cutting-edge techniques to deliver results
          that stand apart in quality and performance.
        </p>
        <div className="why-grid">
          {reasons.map((r, i) => (
            <div className="why-card" key={i}>
              <div className="why-icon"><Ico name={r.icon} size={22} /></div>
              <h3 className="why-title">{r.title}</h3>
              <p className="why-desc">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Gallery Preview ── */
function GalleryPreview() {
  const preview = [...autoImgs.slice(3), ...burnerImgs.slice(0, 5)];

  return (
    <section className="section gallery-section" id="gallery-preview">
      <div className="section-inner">
        <div className="gallery-header">
          <div className="section-tag" style={{ justifyContent: 'center' }}>
            <span className="section-tag-line" />
            Our Work
          </div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Precision in Every <span className="gold">Project</span>
          </h2>
          <p className="section-sub gallery-sub">
            A showcase of our craftsmanship, fabrication quality, and engineering precision.
          </p>
        </div>

        <div className="gallery-grid">
          {preview.map((src, i) => (
            <div className={`gallery-item${i % 4 === 0 ? ' gallery-item-wide' : ''}`} key={i}>
              <img src={src} alt={`Project ${i + 1}`} loading="lazy" />
              <div className="gallery-overlay" />
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Link to="/gallery" className="btn-primary btn-gold">
            View Full Gallery →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Industries Preview ── */
function Industries() {
  const list = [
    { icon: 'palette',   title: 'Glass Artists & Studios' },
    { icon: 'building',  title: 'Manufacturing & Fabrication Shops' },
    { icon: 'flask',     title: 'Research & Development Teams' },
    { icon: 'lightbulb', title: 'Custom Product Innovators' },
  ];

  return (
    <section className="section industries" id="industries">
      <div className="section-inner">
        <div style={{ textAlign: 'center', marginBottom: 0 }}>
          <div className="section-tag" style={{ justifyContent: 'center' }}>
            <span className="section-tag-line" />
            Industries We Serve
          </div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Who We Work <span className="blue">With</span>
          </h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            Serving professionals across industries who demand the highest standards.
          </p>
        </div>
        <div className="industries-grid">
          {list.map((item, i) => (
            <div className="industry-card" key={i}>
              <div className="industry-icon"><Ico name={item.icon} size={36} /></div>
              <div className="industry-title">{item.title}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Link to="/industries" className="btn-primary">
            See All Industries →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── CTA / Contact ── */
function CTA() {
  return (
    <section className="cta" id="contact">
      <div className="cta-bg" />
      <div className="cta-inner">
        <div className="section-tag" style={{ justifyContent: 'center', color: 'rgba(255,255,255,0.6)' }}>
          <span className="section-tag-line" />
          Get In Touch
        </div>
        <h2>Have a Project in Mind?<br />Let's Build It Together.</h2>
        <p className="cta-sub">
          Contact us today to discuss your requirements or request a custom quote.
          We're ready to bring your ideas to life.
        </p>

        <div className="cta-contacts">
          <div className="cta-contact-item">
            <div className="cta-contact-icon"><Ico name="whatsapp" size={20} /></div>
            <div>
              <div className="cta-contact-label">WhatsApp</div>
              <div className="cta-contact-value">+1 (416) 417-2091</div>
            </div>
          </div>
          <div className="cta-contact-item">
            <div className="cta-contact-icon"><Ico name="mappin" size={20} /></div>
            <div>
              <div className="cta-contact-label">Location</div>
              <div className="cta-contact-value">Ontario, Canada</div>
            </div>
          </div>
          <div className="cta-contact-item">
            <div className="cta-contact-icon"><Ico name="mail" size={20} /></div>
            <div>
              <div className="cta-contact-label">Email</div>
              <div className="cta-contact-value">info@ratnamtecho.com</div>
            </div>
          </div>
        </div>

        <a
          href="https://wa.me/14164172091"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-btn cta-btn-whatsapp"
        >
          <Ico name="whatsapp" size={22} />
          Chat on WhatsApp
        </a>
      </div>
    </section>
  );
}
